import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { searchTransferLocations } from '../../../api/endpoints/transfer';
import type { TransferLocation } from '../../../api/types/transfer';

/** Pickup/dropoff autocomplete backed by Transfer/Location/Search — debounced search-as-you-type. */
export function LocationAutocomplete({
  label,
  value,
  onChange,
}: {
  label: string;
  value: TransferLocation | null;
  onChange: (location: TransferLocation | null) => void;
}) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<TransferLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!inputValue || inputValue.length < 3) {
      setOptions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchTransferLocations(inputValue);
        setOptions(res.locations ?? []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  const mergedOptions = useMemo(() => {
    if (value && !options.some((o) => o.locId === value.locId)) return [value, ...options];
    return options;
  }, [options, value]);

  return (
    <Autocomplete
      value={value}
      onChange={(_e, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
      options={mergedOptions}
      getOptionLabel={(option) => option.locName ?? ''}
      isOptionEqualToValue={(option, val) => option.locId === val.locId}
      loading={loading}
      filterOptions={(x) => x}
      noOptionsText={inputValue.length < 3 ? 'Aramak için en az 3 karakter girin' : 'Sonuç bulunamadı'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
